import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardView, dashboardFor, agentInPlan, useHoldStore } from '@elva/ui';
import type { Period } from '@elva/ui';

/** Where the last choice is remembered (spec 17 §3). The URL still wins. */
const STORE_KEY = 'elva.dashboard.period';

function isPeriod(v: string | null): v is Period {
  return v === '24h' || v === '30d';
}

/**
 * The Dashboard (spec 17). `/dashboard?period=24h|30d`.
 *
 * **The selection is in the URL** so a link means the same thing for everybody,
 * and the last choice is remembered per user in local storage — but **the URL wins
 * when present**, which is the whole reason the parameter exists.
 */
export function DashboardPage() {
  const [params, setParams] = useSearchParams();
  const fromUrl = params.get('period');

  // Read the remembered choice only when the URL is silent. localStorage can throw
  // in a locked-down browser, so it is never allowed to break the page.
  const remembered = (() => {
    try {
      const v = window.localStorage.getItem(STORE_KEY);
      return isPeriod(v) ? v : undefined;
    } catch {
      return undefined;
    }
  })();

  const period: Period = isPeriod(fromUrl) ? fromUrl : (remembered ?? '24h');

  // Put the resolved period in the URL, so what is shared is what is shown. This
  // replaces rather than pushes — the first load is not a navigation.
  useEffect(() => {
    if (!isPeriod(fromUrl)) {
      const next = new URLSearchParams(params);
      next.set('period', period);
      setParams(next, { replace: true });
    }
  }, [fromUrl, params, period, setParams]);

  const onPeriodChange = useCallback(
    (next: Period) => {
      try {
        window.localStorage.setItem(STORE_KEY, next);
      } catch {
        // A remembered preference is a convenience; losing it is not an error.
      }
      const p = new URLSearchParams(params);
      p.set('period', next);
      setParams(p);
    },
    [params, setParams],
  );

  // Without Gap Filler (a ?plan=core practice, spec 42 §3), the schedule card
  // shows rings and hours only.
  const plan = useHoldStore().plan;
  const hasGapFiller = agentInPlan('gap-filler', plan);

  return (
    <DashboardView data={dashboardFor(period)} onPeriodChange={onPeriodChange} hasGapFiller={hasGapFiller} />
  );
}
